import { useEffect, useMemo, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useChildren } from '@/hooks/useChildren';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { formatBirthDateWithAge } from '@/utils/formatChildAge';
import { useLingui } from '@lingui/react';
import { EntityLabels } from '@/components/ui/entity-labels';

interface ChildSelectorProps {
  value: string;
  onChange: (childId: string) => void;
}

interface ChildCommandItemProps {
  child: {
    id?: string;
    firstname?: string;
    lastname?: string;
    birthdate?: string;
    avatarUrl?: string;
    childInternalId?: string;
    labels?: Array<{ id?: string; label?: string; color?: string }>;
  };
  isSelected: boolean;
  onSelect: () => void;
  formatBirthDate: (birthDate: string | undefined) => string;
  getInitials: (firstname: string, lastname: string) => string;
}

function ChildCommandItem({
  child,
  isSelected,
  onSelect,
  formatBirthDate,
  getInitials,
}: ChildCommandItemProps) {
  const avatarUrlRaw = child.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;
  const needsImageResolution = isImageId(avatarUrl);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined;
  }

  return (
    <CommandItem
      value={child.id || ''}
      onSelect={onSelect}
      className="p-2"
    >
      <div className="flex items-center gap-3 w-full">
        {/* Avatar with initials - 48px size matching contract wizard */}
        <Avatar className="h-12 w-12 flex-shrink-0">
          {finalAvatarUrl && (
            <AvatarImage
              src={finalAvatarUrl}
              alt={`${child.firstname} ${child.lastname}`}
            />
          )}
          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
            {getInitials(child.firstname || '', child.lastname || '')}
          </AvatarFallback>
        </Avatar>

        {/* Child info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="font-medium text-sm leading-tight truncate">
                {child.firstname} {child.lastname}
                {child.childInternalId && (
                  <span className="text-muted-foreground font-normal"> ({child.childInternalId})</span>
                )}
              </h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatBirthDate(child.birthdate)}
              </p>
              <EntityLabels labels={child.labels} size="sm" maxVisible={2} />
            </div>

            {/* Selected indicator */}
            {isSelected && (
              <div className="ml-2 flex-shrink-0">
                <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CommandItem>
  );
}

export function ChildSelector({ value, onChange }: ChildSelectorProps) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query with 300ms delay
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const { data: childrenData, isLoading } = useChildren(50, 0, {
    withContracts: true,
    search: debouncedQuery || undefined,
  });

  // Reset search query when popover closes
  useEffect(() => {
    if (!open) setSearchQuery('');
  }, [open]);

  const children = useMemo(() => (Array.isArray(childrenData) ? childrenData : []), [childrenData]);

  // Find selected child
  const selectedChild = useMemo(
    () => children.find((child) => child.id === value),
    [children, value]
  );

  // Image resolution for selected child in trigger
  const selectedAvatarUrlRaw = selectedChild?.avatarUrl;
  const selectedAvatarUrl = selectedAvatarUrlRaw?.trim() || undefined;
  const selectedNeedsImageResolution = isImageId(selectedAvatarUrl);
  const selectedResolvedImageUrl = useImageUrl(selectedNeedsImageResolution ? selectedAvatarUrl : null);

  const selectedFinalAvatarUrl = useMemo(() => {
    if (selectedNeedsImageResolution) {
      return selectedResolvedImageUrl || undefined;
    } else if (isDirectImageUrl(selectedAvatarUrl)) {
      return selectedAvatarUrl;
    }
    return undefined;
  }, [selectedNeedsImageResolution, selectedResolvedImageUrl, selectedAvatarUrl]);

  const getInitials = (firstname: string, lastname: string) => {
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase();
  };

  const formatBirthDate = (birthDate: string | undefined) => {
    return formatBirthDateWithAge(birthDate, i18n.locale, i18n);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedChild ? (
            <span className="flex items-center gap-2 truncate">
              <Avatar className="h-5 w-5">
                {selectedFinalAvatarUrl && (
                  <AvatarImage src={selectedFinalAvatarUrl} alt={`${selectedChild.firstname} ${selectedChild.lastname}`} />
                )}
                <AvatarFallback className="text-xs">
                  {getInitials(selectedChild.firstname || '', selectedChild.lastname || '')}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">
                {selectedChild.firstname} {selectedChild.lastname}
                {selectedChild.childInternalId && ` (${selectedChild.childInternalId})`}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {i18n._({ id: 'capacity.childSelector.placeholder', message: 'Select a child...' })}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={i18n._({ id: 'capacity.childSelector.search', message: 'Search children...' })}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? i18n._({ id: 'capacity.childSelector.loading', message: 'Loading children...' })
                : i18n._({ id: 'capacity.childSelector.noResults', message: 'No children found' })}
            </CommandEmpty>
            <CommandGroup>
              {children.map((child) => (
                <ChildCommandItem
                  key={child.id}
                  child={child}
                  isSelected={value === child.id}
                  onSelect={() => {
                    onChange(child.id === value ? '' : child.id || '');
                    setOpen(false);
                  }}
                  formatBirthDate={formatBirthDate}
                  getInitials={getInitials}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ChildSelector;
